/**
 * User-level Narrator Mode reading preferences (localStorage).
 */

import {
  parseSpeakerPaletteId,
  type SpeakerPaletteId,
} from "./speakerPalette";

export const NARRATOR_PREFS_STORAGE_KEY = "desktop-v2.narratorMode.prefs";

export type NarratorReadingPrefs = {
  fontFamily: string;
  fontSizePx: number;
  lineHeight: number;
  maxMeasureCh: number;
  highlightsEnabled: boolean;
  paletteId: SpeakerPaletteId;
  /** Punch-back lead-in seconds (0.5–5). */
  punchBackSec: number;
  /** Target RMS dB for chapter masters (ACX mid). */
  targetRmsDb: number;
};

export const DEFAULT_NARRATOR_PREFS: NarratorReadingPrefs = {
  fontFamily: '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", Palatino, Georgia, serif',
  fontSizePx: 24,
  lineHeight: 1.7,
  maxMeasureCh: 62,
  highlightsEnabled: true,
  paletteId: "pastel",
  punchBackSec: 2,
  targetRmsDb: -20,
};

export const NARRATOR_FONT_OPTIONS: Array<{ id: string; label: string; css: string }> = [
  {
    id: "palatino",
    label: "Palatino",
    css: '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", Palatino, Georgia, serif',
  },
  {
    id: "georgia",
    label: "Georgia",
    css: "Georgia, 'Times New Roman', serif",
  },
  {
    id: "atkinson",
    label: "System UI (high legibility)",
    css: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  },
  {
    id: "libre",
    label: "Libre Baskerville",
    css: '"Libre Baskerville", Georgia, serif',
  },
  {
    id: "crimson",
    label: "Crimson Text",
    css: '"Crimson Text", Georgia, serif',
  },
];

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

export function normalizeNarratorPrefs(raw: unknown): NarratorReadingPrefs {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_NARRATOR_PREFS };
  const rec = raw as Record<string, unknown>;
  const fontFamily =
    typeof rec.fontFamily === "string" && rec.fontFamily.trim()
      ? rec.fontFamily.trim()
      : DEFAULT_NARRATOR_PREFS.fontFamily;
  const fontSizePx =
    typeof rec.fontSizePx === "number" && Number.isFinite(rec.fontSizePx)
      ? clamp(Math.round(rec.fontSizePx), 16, 42)
      : DEFAULT_NARRATOR_PREFS.fontSizePx;
  const lineHeight =
    typeof rec.lineHeight === "number" && Number.isFinite(rec.lineHeight)
      ? clamp(rec.lineHeight, 1.3, 2.4)
      : DEFAULT_NARRATOR_PREFS.lineHeight;
  const maxMeasureCh =
    typeof rec.maxMeasureCh === "number" && Number.isFinite(rec.maxMeasureCh)
      ? clamp(Math.round(rec.maxMeasureCh), 40, 90)
      : DEFAULT_NARRATOR_PREFS.maxMeasureCh;
  const highlightsEnabled =
    typeof rec.highlightsEnabled === "boolean"
      ? rec.highlightsEnabled
      : DEFAULT_NARRATOR_PREFS.highlightsEnabled;
  const paletteId = parseSpeakerPaletteId(rec.paletteId);
  const punchBackSec =
    typeof rec.punchBackSec === "number" && Number.isFinite(rec.punchBackSec)
      ? clamp(rec.punchBackSec, 0.5, 5)
      : DEFAULT_NARRATOR_PREFS.punchBackSec;
  const targetRmsDb =
    typeof rec.targetRmsDb === "number" && Number.isFinite(rec.targetRmsDb)
      ? clamp(rec.targetRmsDb, -23, -18)
      : DEFAULT_NARRATOR_PREFS.targetRmsDb;
  return {
    fontFamily,
    fontSizePx,
    lineHeight,
    maxMeasureCh,
    highlightsEnabled,
    paletteId,
    punchBackSec,
    targetRmsDb,
  };
}

export function loadNarratorPrefs(): NarratorReadingPrefs {
  if (typeof localStorage === "undefined") return { ...DEFAULT_NARRATOR_PREFS };
  try {
    const raw = localStorage.getItem(NARRATOR_PREFS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_NARRATOR_PREFS };
    return normalizeNarratorPrefs(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_NARRATOR_PREFS };
  }
}

export function saveNarratorPrefs(prefs: NarratorReadingPrefs): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(NARRATOR_PREFS_STORAGE_KEY, JSON.stringify(normalizeNarratorPrefs(prefs)));
  } catch {
    // ignore quota / private mode
  }
}
